/**
 * Wraps a navigation so the modal is closed first and the route changes after
 * — and so the modal is closed ONLY when there is a route to change to.
 *
 * Why the order matters: `ps-modal` lives in a `ps-portal-destination`, outside
 * the React tree of the page, so a route change does not remove it. Left open,
 * it stays on top of the next page with its scroll lock on, and its Cancel is
 * bound to a handler of the page that just unmounted.
 *
 * Why the route is resolved first: closing discards unsaved edits in the form,
 * without asking. A target that leads nowhere (a channel with no solution, an
 * unknown project) would otherwise close the modal and stay on the page — the
 * edits gone and nothing to show for it. With no route this does nothing.
 *
 * `onClose` only writes `open = false` on the modal ref
 * (`useGenericModal.closeModal`), so nothing is unmounted between the two calls
 * and `navigate` still runs on the live router. Unsaved edits are still
 * discarded when there IS a route; this does not ask first.
 */
export const closeThenNavigate =
  <Args extends unknown[]>(
    onClose: (() => void) | undefined,
    resolveRoute: (...args: Args) => string | null,
    navigate: (route: string) => void
  ) =>
  (...args: Args): void => {
    const route = resolveRoute(...args);
    if (!route) return;
    onClose?.();
    navigate(route);
  };
